import { useCallback, useEffect, useState } from 'react';
import { getInitialWidth } from '@/redux/modules/windowSize';
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect';
import { useSelector } from 'react-redux';

/**
 * React Hook that returns the current window size
 * and report updates from the 'resize' window event
 *
 * @typedef {WindowSize} WindowSize
 * @property {number} width Window width
 * @property {number} height Window height
 * @returns {WindowSize} An object container the window width and height, and is mobile
 */

type WindowSize = {
    height: number;
    width: number;
};

const useWindowSize = (): WindowSize => {
    const initialWidth = useSelector(getInitialWidth);
    const initialHeight = 768;

    const [windowHeight, setWindowHeight] = useState<number>(initialHeight);
    const [windowWidth, setWindowWidth] = useState<number>(initialWidth);

    useIsomorphicLayoutEffect(() => {
        setWindowHeight(window.innerHeight);
        setWindowWidth(window.innerWidth);
    }, []);

    const handleResize = useCallback(() => {
        setWindowHeight(window.innerHeight);
        setWindowWidth(window.innerWidth);
    }, []);

    useEffect(() => {
        // Add event listener for window resize events
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        // Remove event listener
        return () => {
            window.removeEventListener('resize', handleResize);
            window.addEventListener('orientationchange', handleResize);
        };
    }, [handleResize]);

    return { height: windowHeight, width: windowWidth };
};

export default useWindowSize;
